import customersService from '@/services/customers';
import suppliersService from '@/services/suppliers';
import generalCatalogsService from '@/services/generalCatalogs';
import { extractCollection } from '@/utils/dataNormalization';
import { normalizeCatalogOptions as normalizeCatalogOptionsUtil } from '@/utils/catalogOptions';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: billingDataText } = useDomainText('billingData');

const resolveContributorOwnerContext = (vm) => {
  const customerId = Number(vm?.customerId || 0);
  if (customerId > 0) {
    return {
      type: 'customer',
      ownerId: customerId,
      service: customersService,
    };
  }

  const supplierId = Number(vm?.supplierId || 0);
  if (supplierId > 0) {
    return {
      type: 'supplier',
      ownerId: supplierId,
      service: suppliersService,
    };
  }

  return {
    type: '',
    ownerId: 0,
    service: null,
  };
};

export const contributorMethods = {
  openContributorListModal() {
    this.isContributorListModalOpen = true;
  },
  handleOpenBillingDataList() {
    this.openContributorListModal();
  },
  closeContributorListModal() {
    this.isContributorListModalOpen = false;
  },
  async handleEditContributorFromList(contributor) {
    if (!contributor?.id) {
      return;
    }

    this.closeContributorListModal();
    await this.openContributorModal('edit', contributor);
  },
  async handleDeleteContributor(contributor) {
    if (!contributor?.id) {
      return;
    }

    const context = resolveContributorOwnerContext(this);

    if (context.type === 'customer') {
      this.pendingActionType = 'delete-contributor';
      this.pendingActionCustomer = null;
      this.pendingActionPerson = null;
      this.pendingActionContributor = contributor;
      this.isConfirmActionModalOpen = true;
      return;
    }

    if (!context.ownerId || !context.service) {
      return;
    }

    const contributorRfc = String(contributor?.rfc || '').trim() || billingDataText.confirm.fallbackRfc;
    const contributorName = String(
      contributor?.name
      || contributor?.business_name
      || contributor?.fiscal_name
      || ''
    ).trim() || billingDataText.confirm.fallbackFiscalName;

    const shouldDelete = window.confirm(billingDataText.confirm.messageDelete({
      rfc: contributorRfc,
      fiscalName: contributorName,
    }));

    if (!shouldDelete) {
      return;
    }

    try {
      await context.service.contributorsDelete(context.ownerId, contributor.id);
      this.$toast?.success(billingDataText.toast.deleteSuccess);
      await this.fetchContributors();
    } catch (error) {
      const message = error?.response?.data?.message || billingDataText.toast.deleteError;
      this.$toast?.error(message);
    }
  },
  async handleCreateBillingData() {
    await this.openContributorModal('create', null);
  },
  async ensureBillingCatalogsLoaded() {
    if (
      this.regimenOptions.length > 0
      && this.usoCfdiOptions.length > 0
      && this.paymentMethodOptions.length > 0
      && this.paymentMethodTypeOptions.length > 0
    ) {
      return;
    }

    const [regimensResult, usosResult, formasResult, metodosResult] = await Promise.allSettled([
      generalCatalogsService.getRegimens(),
      generalCatalogsService.getUsosCfdi(),
      generalCatalogsService.getFormasPago(),
      generalCatalogsService.getMetodosPago(),
    ]);

    if (regimensResult.status === 'fulfilled') {
      this.regimenOptions = normalizeCatalogOptionsUtil(extractCollection(regimensResult.value));
    }

    if (usosResult.status === 'fulfilled') {
      this.usoCfdiOptions = normalizeCatalogOptionsUtil(extractCollection(usosResult.value));
    }

    if (formasResult.status === 'fulfilled') {
      this.paymentMethodOptions = normalizeCatalogOptionsUtil(extractCollection(formasResult.value));
    }

    if (metodosResult.status === 'fulfilled') {
      this.paymentMethodTypeOptions = normalizeCatalogOptionsUtil(extractCollection(metodosResult.value));
    }
  },
  async openContributorModal(mode = 'edit', initialData = null) {
    await Promise.all([
      this.ensureGeoCatalogsLoaded(),
      this.ensureBillingCatalogsLoaded(),
    ]);

    this.contributorModalMode = String(mode || 'create').toLowerCase() === 'edit' ? 'edit' : 'create';
    this.contributorModalInitialData = initialData && typeof initialData === 'object' ? { ...initialData } : null;
    this.isContributorModalOpen = true;
  },
  closeContributorModal() {
    this.isContributorModalOpen = false;
    this.contributorModalMode = 'create';
    this.contributorModalInitialData = null;
    this.isContributorSubmitting = false;
  },
  handleDeleteContributorFromModal(contributor) {
    if (!contributor?.id) {
      return;
    }

    this.closeContributorModal();
    this.handleDeleteContributor(contributor);
  },
  handleEditBillingData() {
    const currentContributor = this.defaultBillingData;

    if (currentContributor && currentContributor.id) {
      this.openContributorModal('edit', currentContributor);
      return;
    }

    this.openContributorModal('create', null);
  },
  async submitContributor(payload) {
    const context = resolveContributorOwnerContext(this);
    if (!context.ownerId || !context.service) {
      return;
    }

    this.isContributorSubmitting = true;

    try {
      if (this.contributorModalMode === 'edit' && this.contributorModalInitialData?.id) {
        await context.service.contributorsUpdate(
          context.ownerId,
          this.contributorModalInitialData.id,
          payload || {}
        );
        this.$toast?.success(billingDataText.toast.updateSuccess);
      } else {
        await context.service.contributorsStore(context.ownerId, payload || {});
        this.$toast?.success(billingDataText.toast.createSuccess);
      }

      this.closeContributorModal();
      await this.fetchContributors();
    } catch (error) {
      const message = error?.response?.data?.message || billingDataText.toast.saveError;
      this.$toast?.error(message);
    } finally {
      this.isContributorSubmitting = false;
    }
  },
  async fetchContributors() {
    const context = resolveContributorOwnerContext(this);
    if (!context.ownerId || !context.service) {
      this.contributors = [];
      return;
    }

    try {
      const response = await context.service.contributorsIndex(context.ownerId);
      this.contributors = extractCollection(response);
    } catch (error) {
      this.contributors = [];
    }
  },
};
